import type { Policy, CreatePolicyInput, UpdatePolicyInput } from '@communities/content-management/domain';
import { useStrikePolicies } from '@communities/content-management/hooks';
import { DataList, type TableColumn, type DeleteItemContext, SectionHeader } from '@core/components';
import { Badge, BadgeVariant, Button, Section } from '@soteria/design-system-ve';
import { useState, useCallback, useMemo } from 'react';

import { StrikePolicyDialog } from './StrikePolicyDialog';

interface StrikePolicySectionProps {
  communityId: string;
  isEntityAdmin: boolean;
}

/**
 * Section for managing strike policies with full CRUD operations.
 * Shows the list of strike policies with threshold-based configuration using DataList.
 * Strike policies are triggered when accumulated points reach the strikeThreshold,
 * unlike classification/moderation policies which are label-based.
 *
 * Supports:
 * - Creating new strike policies via dialog
 * - Editing existing policies via DataList row edit
 * - Deleting policies with confirmation dialog
 */
export function StrikePolicySection({ communityId, isEntityAdmin }: StrikePolicySectionProps) {
  const { policies, loading, errors, createPolicy, updatePolicy, deletePolicy, resetErrors } = useStrikePolicies(communityId);

  // Dialog state management
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | undefined>(undefined);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Collect existing thresholds for uniqueness validation
  const existingThresholds = useMemo(() => policies.map(p => p.strikeThreshold ?? 0).filter(t => t > 0), [policies]);

  const columns: TableColumn<Policy>[] = [
    {
      key: 'name',
      header: 'Name',
      render: policy => policy.name || `Policy ${policy.strikeThreshold ?? 0}pts`,
      width: '2fr',
    },
    {
      key: 'description',
      header: 'Description',
      render: policy => policy.description || '-',
      width: '2fr',
    },
    {
      key: 'threshold',
      header: 'Threshold',
      render: policy => `${policy.strikeThreshold ?? '-'} points`,
      width: '1fr',
    },
    {
      key: 'status',
      header: 'Status',
      render: policy => <Badge text={policy.isEnabled ? 'Enabled' : 'Disabled'} variant={policy.isEnabled ? BadgeVariant.Success : BadgeVariant.Error} />,
      width: '1fr',
    },
  ];

  // Open dialog for create or edit
  const handleOpenDialog = useCallback(
    (policy?: Policy, mode: 'create' | 'edit' = 'create') => {
      setSelectedPolicy(policy);
      setDialogMode(mode);
      setIsDialogOpen(true);
      resetErrors();
    },
    [resetErrors]
  );

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setSelectedPolicy(undefined);
    setDialogMode('create');
    resetErrors();
  }, [resetErrors]);

  // Save handler for create and edit operations
  const handleSave = useCallback(
    async (input: CreatePolicyInput): Promise<boolean> => {
      if (dialogMode === 'edit' && selectedPolicy) {
        const updateInput: UpdatePolicyInput = {
          name: input.name,
          description: input.description,
          isEnabled: input.isEnabled,
          workflowType: input.workflowType,
          labels: input.labels,
          strikeThreshold: input.strikeThreshold,
        };

        return updatePolicy(communityId, selectedPolicy.id, updateInput);
      }

      return createPolicy(communityId, input);
    },
    [dialogMode, selectedPolicy, communityId, createPolicy, updatePolicy]
  );

  // Delete confirmation context for DataList
  const deleteItemContext: DeleteItemContext<Policy> | undefined = isEntityAdmin
    ? {
        handler: async (item: Policy) => await deletePolicy(communityId, item.id),
        isDeleting: loading,
        errors,
        resetErrors,
      }
    : undefined;

  return (
    <>
      <Section spacing="large">
        <SectionHeader
          description="Strike policies define automated actions when users reach strike point thresholds."
          endComponent={isEntityAdmin ? <Button onClick={() => handleOpenDialog()}>ADD POLICY</Button> : undefined}
          title={`Strike Policies (${policies.length})`}
        />

        <DataList
          columns={columns}
          data={policies}
          dataTypeName="strike policy"
          deleteItemContext={deleteItemContext}
          error={errors}
          isLoading={loading}
          onEdit={isEntityAdmin ? policy => handleOpenDialog(policy, 'edit') : undefined}
        />
      </Section>

      <StrikePolicyDialog
        communityId={communityId}
        errors={errors}
        existingThresholds={existingThresholds}
        isOpen={isDialogOpen}
        mode={dialogMode}
        policy={selectedPolicy}
        onClose={handleCloseDialog}
        onSave={handleSave}
      />
    </>
  );
}
